import { getBackendUrl } from '../config/api';
import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import EmployeesManagement from './EmployeesManagement';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { 
  Users, 
  Shield, 
  Calendar,
  Settings,
  Edit,
  Mail,
  User,
  AlertTriangle,
  Eye,
  EyeOff,
  ChevronDown,
  ChevronRight
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = getBackendUrl();
const API = `${BACKEND_URL}/api`;

const AdminPanel = () => {
  const { user: currentUser, isSuperAdmin } = useAuth();
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') || 'members';
  
  const [activeTab, setActiveTab] = useState(initialTab);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [collapsedTabs, setCollapsedTabs] = useState({});
  
  // Time Slots Management
  const [timeSlots, setTimeSlots] = useState([
    { id: 1, startTime: '09:00', endTime: '10:30', label: 'Slot 1', description: 'Morning Session 1' },
    { id: 2, startTime: '10:30', endTime: '12:00', label: 'Slot 2', description: 'Morning Session 2' },
    { id: 3, startTime: '12:00', endTime: '13:30', label: 'Slot 3', description: 'Pre-Lunch Session' },
    { id: 4, startTime: '13:30', endTime: '15:00', label: 'Slot 4', description: 'Post-Lunch Session' },
    { id: 5, startTime: '15:00', endTime: '16:30', label: 'Slot 5', description: 'Afternoon Session 1' },
    { id: 6, startTime: '16:30', endTime: '18:00', label: 'Slot 6', description: 'Afternoon Session 2' }
  ]);
  const [editingSlot, setEditingSlot] = useState(null);
  const [isEditSlotModalOpen, setIsEditSlotModalOpen] = useState(false);
  
  // Super Admin Settings
  const [superAdminInfo, setSuperAdminInfo] = useState(null);
  const [isChangeEmailModalOpen, setIsChangeEmailModalOpen] = useState(false);
  const [emailFormData, setEmailFormData] = useState({
    oldEmail: '',
    newEmail: '',
    confirmEmail: '',
    currentPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);

  const getUsersByRole = (role) => {
    return allUsers.filter(user => user.role === role);
  };

  const tabs = [
    { 
      id: 'members', 
      label: 'Members', 
      icon: Users, 
      count: allUsers.length,
      collapsible: true 
    },
    ...(isSuperAdmin() ? [
      { 
        id: 'super_admin_management', 
        label: 'Super Admin Settings', 
        icon: Shield, 
        count: null,
        collapsible: true 
      }
    ] : []),
    // Time slots only accessible via URL parameter (from Smart Masters menu)
    ...(initialTab === 'time_slots' ? [
      { 
        id: 'time_slots', 
        label: 'Time Slots', 
        icon: Calendar, 
        count: null,
        collapsible: true 
      }
    ] : []),
  ];

  const handleTabClick = (tabId) => {
    setActiveTab(tabId);
    // Initialize collapsed state if undefined
    setCollapsedTabs(prev => ({
      ...prev,
      [tabId]: false // Always expand when selecting a tab
    }));
  };

  const fetchAllUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/auth/all-users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAllUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to load users');
    }
  };

  const fetchData = async () => {
    setLoading(true);
    await fetchAllUsers();
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
    if (isSuperAdmin()) {
      fetchSuperAdminInfo();
    }
  }, []);

  // Moved getUsersByRole above tabs definition

  const handleEditTimeSlot = (slot) => {
    setEditingSlot({ ...slot });
    setIsEditSlotModalOpen(true);
  };

  const handleSaveTimeSlot = () => {
    const updatedSlots = timeSlots.map(slot => 
      slot.id === editingSlot.id ? editingSlot : slot
    );
    setTimeSlots(updatedSlots);
    setIsEditSlotModalOpen(false);
    setEditingSlot(null);
    toast.success('Time slot updated successfully');
  };

  const fetchSuperAdminInfo = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/auth/super-admin-info`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuperAdminInfo(response.data);
      setEmailFormData({
        ...emailFormData,
        oldEmail: response.data.email
      });
    } catch (error) {
      console.error('Error fetching super admin info:', error);
      toast.error('Failed to load super admin information');
    }
  };

  const handleChangeEmail = async () => {
    // Validation
    if (!emailFormData.newEmail || !emailFormData.confirmEmail) {
      toast.error('Please fill in all email fields');
      return;
    }

    if (emailFormData.newEmail !== emailFormData.confirmEmail) {
      toast.error('New email and confirmation do not match');
      return;
    }

    if (emailFormData.newEmail === emailFormData.oldEmail) {
      toast.error('New email must be different from current email');
      return;
    }

    // Email format validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(emailFormData.newEmail)) {
      toast.error('Please enter a valid email address');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/auth/change-super-admin-email?old_email=${emailFormData.oldEmail}&new_email=${emailFormData.newEmail}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Super admin email changed successfully! Please login again with your new email.');
      
      // Close modal and logout user to require re-login with new email
      setIsChangeEmailModalOpen(false);
      setTimeout(() => {
        window.location.href = '/login';
      }, 2000);
      
    } catch (error) {
      console.error('Error changing super admin email:', error);
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error('Failed to change super admin email');
      }
    }
  };

  const openChangeEmailModal = () => {
    setEmailFormData({
      oldEmail: superAdminInfo?.email || '',
      newEmail: '',
      confirmEmail: '',
      currentPassword: ''
    });
    setIsChangeEmailModalOpen(true);
  };

  const renderSuperAdminSettings = () => (
    <div className="space-y-6">
      {/* Super Admin Information */}
      <Card className="border-purple-200">
        <CardHeader className="bg-purple-50">
          <CardTitle className="flex items-center">
            <User className="w-5 h-5 mr-2 text-purple-600" />
            Account Information
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          {superAdminInfo && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Name</label>
                  <div className="p-3 bg-gray-50 border border-gray-200 rounded-md">
                    <div className="flex items-center">
                      <User className="w-4 h-4 text-gray-500 mr-2" />
                      <span className="text-gray-900">{superAdminInfo.name}</span>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Email Address</label>
                  <div className="p-3 bg-gray-50 border border-gray-200 rounded-md">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <Mail className="w-4 h-4 text-gray-500 mr-2" />
                        <span className="text-gray-900">{superAdminInfo.email}</span>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={openChangeEmailModal}
                        className="text-purple-600 border-purple-200 hover:bg-purple-50"
                      >
                        <Settings className="w-3 h-3 mr-1" />
                        Change
                      </Button>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Role</label>
                  <div className="p-3 bg-purple-50 border border-purple-200 rounded-md">
                    <div className="flex items-center">
                      <Shield className="w-4 h-4 text-purple-600 mr-2" />
                      <span className="text-purple-800 font-medium">Super Administrator</span>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Account Created</label>
                  <div className="p-3 bg-gray-50 border border-gray-200 rounded-md">
                    <span className="text-gray-900">
                      {new Date(superAdminInfo.created_at).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Security Warning */}
      <Card className="border-orange-200">
        <CardContent className="p-6">
          <div className="flex items-start space-x-3">
            <AlertTriangle className="w-5 h-5 text-orange-500 mt-0.5" />
            <div>
              <h3 className="font-medium text-orange-800">Important Security Notes</h3>
              <ul className="mt-2 text-sm text-orange-700 space-y-1">
                <li>• Changing your email will require you to login again with the new email address</li>
                <li>• Ensure you have access to the new email address before making changes</li>
                <li>• Your super admin privileges will be maintained with the new email</li>
                <li>• Keep your super admin credentials secure and do not share them</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  const renderTimeSlots = () => (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center">
              <Calendar className="w-5 h-5 mr-2 text-blue-600" />
              Time Slots Configuration
            </div>
            <Button
              onClick={() => toast.info('Time slot changes are applied instantly to new schedules')}
              variant="outline"
              size="sm"
              className="text-blue-600 border-blue-200 hover:bg-blue-50"
            >
              <Settings className="w-4 h-4 mr-2" />
              Settings
            </Button>
          </CardTitle>
          <p className="text-sm text-gray-600 mt-1">
            Configure the standard time slots used throughout the system for scheduling classes and sessions.
          </p>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {timeSlots.map((timeSlot) => (
              <div key={timeSlot.id} className="text-center p-6 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg border border-blue-200 hover:shadow-md transition-shadow group">
                <div className="text-lg font-bold text-blue-900 mb-2">{timeSlot.startTime}-{timeSlot.endTime}</div>
                <div className="text-sm font-medium text-blue-700 mb-1">{timeSlot.label}</div>
                <div className="text-xs text-blue-600 mb-3">{timeSlot.description}</div>
                <Button
                  onClick={() => handleEditTimeSlot(timeSlot)}
                  variant="outline"
                  size="sm"
                  className="opacity-0 group-hover:opacity-100 transition-opacity text-blue-600 border-blue-300 hover:bg-blue-100"
                >
                  <Edit className="w-3 h-3 mr-1" />
                  Edit
                </Button>
              </div>
            ))}
          </div>
          
          <div className="mt-8 p-4 bg-gray-50 rounded-lg border">
            <h4 className="font-medium text-gray-900 mb-2 flex items-center">
              <Settings className="w-4 h-4 mr-2" />
              Configuration Details
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-600">
              <div>
                <strong>Total Duration:</strong> 9 hours (9:00 AM - 6:00 PM)
              </div>
              <div>
                <strong>Session Length:</strong> 1.5 hours per slot
              </div>
              <div>
                <strong>Active Days:</strong> Monday to Saturday
              </div>
              <div>
                <strong>Break Time:</strong> No scheduled breaks between slots
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  const renderUserList = (users, title) => {
    return (
      <div className="space-y-4">
        <Card>
          <CardHeader>
            <CardTitle>{title}</CardTitle>
          </CardHeader>
          <CardContent>
            {users.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No {title.toLowerCase()} found
              </div>
            ) : (
              <div className="space-y-3">
                {users.map(user => (
                  <div key={user.id} className="flex items-center justify-between p-3 border rounded-lg">
                    <div>
                      <div className="font-medium">{user.name}</div>
                      <div className="text-sm text-gray-600">{user.email}</div>
                    </div>
                    <div className="text-sm text-gray-500 capitalize">
                      {user.role.replace('_', ' ')}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  };

  const renderSuperAdminManagement = () => {
    const superAdmins = getUsersByRole('super_admin');
    console.log('renderSuperAdminManagement - allUsers:', allUsers.length);
    console.log('renderSuperAdminManagement - superAdmins:', superAdmins);
    
    return (
      <div className="space-y-6">
        {/* Debug Info */}
        <div className="bg-blue-50 p-3 rounded-lg text-sm">
          <div>Total users: {allUsers.length}</div>
          <div>Super admins: {superAdmins.length}</div>
          <div>Loading: {loading ? 'Yes' : 'No'}</div>
        </div>
        
        {/* Super Administrators List */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <Shield className="w-5 h-5 mr-2 text-purple-600" />
              Super Administrators
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8 text-gray-500">
                Loading super administrators...
              </div>
            ) : superAdmins.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No super administrators found
                <div className="text-xs mt-2 text-gray-400">
                  Total users loaded: {allUsers.length}
                </div>
                <div className="text-xs mt-1 text-gray-400">
                  All users: {allUsers.map(u => `${u.name} (${u.role})`).join(', ')}
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                {superAdmins.map(user => (
                  <div key={user.id} className="flex items-center justify-between p-3 border rounded-lg bg-green-50">
                    <div>
                      <div className="font-medium">{user.name}</div>
                      <div className="text-sm text-gray-600">{user.email}</div>
                    </div>
                    <div className="text-sm text-gray-500 capitalize">
                      {user.role.replace('_', ' ')}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Super Admin Settings */}
        {renderSuperAdminSettings()}
      </div>
    );
  };



  // User Management Tab - Grant/Revoke Admin Rights (SUPER ADMIN ONLY)
  const renderUserManagement = () => {
    // Extra security check - only Super Admins should access this
    if (!isSuperAdmin()) {
      return (
        <Card>
          <CardContent className="py-12 text-center">
            <Shield className="w-16 h-16 mx-auto mb-4 text-red-500" />
            <h3 className="text-xl font-semibold mb-2">Access Restricted</h3>
            <p className="text-gray-600">Only Super Admins can manage user roles and permissions.</p>
          </CardContent>
        </Card>
      );
    }

    const handleRoleChange = async (userId, newRole) => {
      try {
        const token = localStorage.getItem('token');
        await axios.put(
          `${API}/auth/users/${userId}/role`,
          null,
          {
            headers: { Authorization: `Bearer ${token}` },
            params: { role: newRole }
          }
        );
        toast.success(`User role updated to ${newRole}`);
        // Refresh users list
        const response = await axios.get(`${API}/auth/all-users`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setAllUsers(response.data);
      } catch (error) {
        console.error('Error updating user role:', error);
        toast.error(error.response?.data?.detail || 'Failed to update user role');
      }
    };

    const handleStatusChange = async (userId, newStatus) => {
      try {
        const token = localStorage.getItem('token');
        // Backend uses 'approved'/'pending'/'rejected', not 'active'/'inactive'
        const backendStatus = newStatus === 'active' ? 'approved' : 'pending';
        
        await axios.put(
          `${API}/auth/users/${userId}/status`,
          null,
          {
            headers: { Authorization: `Bearer ${token}` },
            params: { status: backendStatus }
          }
        );
        toast.success(`User status updated successfully`);
        // Refresh users list
        const response = await axios.get(`${API}/auth/all-users`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setAllUsers(response.data);
      } catch (error) {
        console.error('Error updating user status:', error);
        toast.error(error.response?.data?.detail || 'Failed to update user status');
      }
    };

    const getRoleBadgeColor = (role) => {
      switch(role) {
        case 'super_admin': return 'bg-purple-100 text-purple-800 border-purple-200';
        case 'admin': return 'bg-blue-100 text-blue-800 border-blue-200';
        case 'teacher': return 'bg-green-100 text-green-800 border-green-200';
        default: return 'bg-gray-100 text-gray-800 border-gray-200';
      }
    };

    const getStatusBadgeColor = (status) => {
      return status === 'active' 
        ? 'bg-green-100 text-green-800 border-green-200' 
        : 'bg-red-100 text-red-800 border-red-200';
    };

    return (
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Shield className="w-5 h-5 text-purple-600" />
              <span>User Role Management</span>
              <Badge className="bg-purple-100 text-purple-800">Super Admin Only</Badge>
            </CardTitle>
            <p className="text-sm text-gray-600 mt-2">
              Grant or revoke admin privileges to users. Activate or deactivate user accounts.
            </p>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {allUsers.map(user => (
                <Card key={user.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4 flex-1">
                        <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-bold text-lg">
                          {user.name?.charAt(0)?.toUpperCase()}
                        </div>
                        <div className="flex-1">
                          <div className="font-semibold text-lg">{user.name}</div>
                          <div className="text-sm text-gray-600">{user.email}</div>
                          <div className="flex items-center space-x-2 mt-2">
                            <Badge className={getRoleBadgeColor(user.role)}>
                              {user.role === 'super_admin' ? 'Super Admin' : 
                               user.role === 'admin' ? 'Admin' : 
                               user.role === 'teacher' ? 'Teacher' : 'User'}
                            </Badge>
                            <Badge className={getStatusBadgeColor(user.status)}>
                              {user.status}
                            </Badge>
                            {user.id === currentUser?.id && (
                              <Badge className="bg-green-100 text-green-800">You</Badge>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      {/* Action Buttons - Only show for other users */}
                      {user.id !== currentUser?.id && (
                        <div className="flex flex-col space-y-2">
                          {/* Role Change Buttons */}
                          <div className="flex space-x-2">
                            {user.role !== 'admin' && user.role !== 'super_admin' && (
                              <Button
                                size="sm"
                                onClick={() => handleRoleChange(user.id, 'admin')}
                                className="bg-blue-600 hover:bg-blue-700 text-white"
                              >
                                <Shield className="w-3 h-3 mr-1" />
                                Make Admin
                              </Button>
                            )}
                            {user.role === 'admin' && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleRoleChange(user.id, 'teacher')}
                                className="border-orange-300 text-orange-700 hover:bg-orange-50"
                              >
                                <User className="w-3 h-3 mr-1" />
                                Remove Admin
                              </Button>
                            )}
                            {user.role === 'super_admin' && (
                              <Badge variant="outline" className="text-xs">
                                Protected Role
                              </Badge>
                            )}
                          </div>
                          
                          {/* Status Change Button - Cannot change Super Admin status */}
                          {user.role !== 'super_admin' && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleStatusChange(
                                user.id, 
                                user.status === 'active' ? 'inactive' : 'active'
                              )}
                              className={user.status === 'active' 
                                ? 'border-red-300 text-red-700 hover:bg-red-50'
                                : 'border-green-300 text-green-700 hover:bg-green-50'
                              }
                            >
                              {user.status === 'active' ? (
                                <>
                                  <EyeOff className="w-3 h-3 mr-1" />
                                  Deactivate
                                </>
                              ) : (
                                <>
                                  <Eye className="w-3 h-3 mr-1" />
                                  Activate
                                </>
                              )}
                            </Button>
                          )}
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
            
            {allUsers.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                <Users className="w-12 h-12 mx-auto mb-2 text-gray-400" />
                <p>No users found</p>
              </div>
            )}
          </CardContent>
        </Card>
        
        {/* Info Card */}
        <Card className="bg-purple-50 border-purple-200">
          <CardContent className="p-4">
            <div className="flex items-start space-x-3">
              <AlertTriangle className="w-5 h-5 text-purple-600 mt-0.5" />
              <div className="text-sm">
                <p className="font-semibold text-purple-900">Important Notes:</p>
                <ul className="list-disc list-inside mt-2 space-y-1 text-purple-800">
                  <li>You cannot change your own role or status</li>
                  <li>Super Admin roles cannot be modified (protected)</li>
                  <li>Deactivated users cannot login to the system</li>
                  <li>Admin users have access to all management features</li>
                  <li>Regular users (teachers) have limited access</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };

  const renderTabContent = () => {
    console.log('AdminPanel renderTabContent - activeTab:', activeTab);
    switch (activeTab) {
      case 'members':
        return <EmployeesManagement />;
      case 'super_admin_management':
        console.log('Rendering super admin management');
        return renderSuperAdminManagement();
      case 'time_slots':
        return renderTimeSlots();
      default:
        return <EmployeesManagement />;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Admin Panel</h1>
          <p className="text-gray-600 mt-1">Comprehensive user and permission management</p>
        </div>
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <Users className="w-5 h-5 text-blue-600" />
            <span className="text-sm font-medium text-gray-700">
              {allUsers.length} Total Users
            </span>
          </div>
        </div>
      </div>

      {/* Sidebar-style Collapsible Interface */}
      <div className="w-full bg-white shadow-xl border border-gray-100 rounded-xl">
        <div className="p-4">
          <nav className="space-y-2">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const isCollapsed = collapsedTabs[tab.id];
              const isActive = activeTab === tab.id;
              
              return (
                <div key={tab.id} className="space-y-1">
                  {/* Section Header - matching Smart Masters exactly */}
                  <button
                    onClick={() => handleTabClick(tab.id)}
                    className={`w-full flex items-center px-4 py-3 text-left rounded-xl transition-all duration-200 group ${
                      isActive
                        ? 'bg-blue-50 text-blue-700 border border-blue-200 shadow-sm'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    }`}
                  >
                    <Icon className={`w-5 h-5 mr-3 transition-colors ${
                      isActive ? 'text-blue-600' : 'text-gray-400 group-hover:text-gray-600'
                    }`} />
                    <span className="font-medium flex-1">{tab.label}</span>
                    {tab.count !== null && (
                      <span className="text-xs bg-gray-200 text-gray-600 px-2 py-1 rounded-full mr-2">
                        {tab.count}
                      </span>
                    )}
                    {isCollapsed ? (
                      <ChevronRight className="w-4 h-4 text-gray-400" />
                    ) : (
                      <ChevronDown className="w-4 h-4 text-gray-400" />
                    )}
                  </button>
                  
                  {/* Section Content - show when active and not collapsed */}
                  {isActive && !isCollapsed && (
                    <div className="ml-6 space-y-4 pt-2">
                      {loading ? (
                        <div className="flex items-center justify-center py-12">
                          <div className="text-lg text-gray-600">Loading...</div>
                        </div>
                      ) : (
                        <div className="bg-gray-50 rounded-lg p-4 border-l-4 border-blue-500">
                          {renderTabContent()}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Edit Time Slot Modal */}
      {isEditSlotModalOpen && editingSlot && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4 flex items-center">
              <Calendar className="w-5 h-5 mr-2 text-blue-600" />
              Edit Time Slot
            </h3>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Start Time
                  </label>
                  <input
                    type="time"
                    value={editingSlot.startTime}
                    onChange={(e) => setEditingSlot(prev => ({ ...prev, startTime: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    End Time
                  </label>
                  <input
                    type="time"
                    value={editingSlot.endTime}
                    onChange={(e) => setEditingSlot(prev => ({ ...prev, endTime: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Label
                </label>
                <input
                  type="text"
                  value={editingSlot.label}
                  onChange={(e) => setEditingSlot(prev => ({ ...prev, label: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., Slot 1"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <input
                  type="text"
                  value={editingSlot.description}
                  onChange={(e) => setEditingSlot(prev => ({ ...prev, description: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., Morning Session 1"
                />
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <div className="flex items-start">
                  <Settings className="w-4 h-4 text-yellow-600 mr-2 mt-0.5 flex-shrink-0" />
                  <div className="text-sm">
                    <p className="text-yellow-800 font-medium">Important Notice</p>
                    <p className="text-yellow-700 mt-1">
                      Changes to time slots will affect future scheduling. Existing sessions will remain unchanged.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="flex space-x-3 mt-6">
              <Button
                variant="outline"
                onClick={() => setIsEditSlotModalOpen(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button 
                onClick={handleSaveTimeSlot}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
              >
                <Calendar className="w-4 h-4 mr-2" />
                Save Changes
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Change Email Modal */}
      <Dialog open={isChangeEmailModalOpen} onOpenChange={setIsChangeEmailModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-2 text-purple-600">
              <Mail className="w-5 h-5" />
              <span>Change Super Admin Email</span>
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <AlertTriangle className="w-5 h-5 text-orange-500 mt-0.5" />
                <div>
                  <h4 className="text-sm font-medium text-orange-800">Critical Action</h4>
                  <p className="text-sm text-orange-700 mt-1">
                    You will be logged out after changing your email and must login again with the new email address.
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Current Email</label>
              <Input
                value={emailFormData.oldEmail}
                disabled
                className="bg-gray-50"
              />
            </div>
            
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">New Email Address</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input
                  type="email"
                  placeholder="Enter new email address"
                  value={emailFormData.newEmail}
                  onChange={(e) => setEmailFormData({...emailFormData, newEmail: e.target.value})}
                  className="pl-10"
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Confirm New Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input
                  type="email"
                  placeholder="Confirm new email address"
                  value={emailFormData.confirmEmail}
                  onChange={(e) => setEmailFormData({...emailFormData, confirmEmail: e.target.value})}
                  className="pl-10"
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsChangeEmailModalOpen(false)}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleChangeEmail}
              className="bg-purple-600 hover:bg-purple-700 text-white"
              disabled={!emailFormData.newEmail || !emailFormData.confirmEmail}
            >
              <Mail className="w-4 h-4 mr-2" />
              Change Email
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPanel;